import React, { useEffect, useState, useCallback } from 'react';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { JobApplication } from '../../types/jobs';
import { useThemeStore } from '../../state/theme';
import SelectComponent, { SelectOption } from '../common/Select';
import PhoneNumberInput from '../common/PhoneNumberInput';
import TagInput from '../common/TagInput';
import { IoAddCircleOutline } from 'react-icons/io5';
import { FaTrashAlt } from 'react-icons/fa';
import FileUpload from '../common/FileUpload';
import { FaEye } from 'react-icons/fa';
import { uploadAsset } from '../../api/aws/aws';
import { languageOptions, fluencyOptions, monthNames } from '../../constants';
import { BtnDefault, BtnPrimary } from '../common/Buttons';
import { useNavigate } from 'react-router-dom';
import { updateJobApplication } from '../../hooks/mutations';
import { UpdateJobApplicationResponse } from '../../api/jobs/updateJobApplication';
import { MultiValue, SingleValue } from 'react-select';
import UrlPreview from '../common/UrlPreview';
import { safeLink } from '../../utils';
import { ArrowLeft, LoaderCircle, X } from 'lucide-react';
import { toast } from 'react-hot-toast';

const UpdateJobApplication: React.FC<{
  currentApplication: JobApplication | undefined;
  onSuccess?: (data: UpdateJobApplicationResponse) => void;
  onError?: (error: Error) => void;
}> = ({ currentApplication, onSuccess, onError }) => {
  const { mutate: updateJobApplicationMutate, isPending } =
    updateJobApplication({
      onSuccess: (data) => onSuccess?.(data),
      onError: (error) => onError?.(error),
    });

  const { isDarkMode } = useThemeStore();
  const navigate = useNavigate();
  const [isResumeUploading, setIsResumeUploading] = useState(false);
  const [isVideoUploading, setIsVideoUploading] = useState(false);
  const methods = useForm<JobApplication>();
  const { register, handleSubmit, reset, control, watch } = methods;

  // State for assets
  const [existingResumes, setExistingResumes] = useState<string[]>([]);
  const [existingVideos, setExistingVideos] = useState<string[]>([]);

  const {
    fields: languageFields,
    append: appendLanguage,
    remove: removeLanguage,
  } = useFieldArray({
    control,
    name: 'questionnaire.languages',
  });

  const {
    fields: workExperienceFields,
    append: appendWorkExperience,
    remove: removeWorkExperience,
  } = useFieldArray({
    control,
    name: 'workExperience.pastEmployers',
  });

  // Initialize form with current application data
  useEffect(() => {
    if (currentApplication) {
      const formattedApplication = {
        ...currentApplication,
        personalInfo: {
          ...currentApplication.personalInfo,
          dob: currentApplication.personalInfo?.dob
            ? new Date(currentApplication.personalInfo.dob)
                .toISOString()
                .split('T')[0]
            : undefined,
        },
      };
      reset(formattedApplication);

      const resumes = [
        ...(currentApplication.workExperience?.resumes || []),
        ...(currentApplication.workExperience?.resume
          ? [currentApplication.workExperience.resume]
          : []),
      ].filter(
        (url): url is string => typeof url === 'string' && url.trim() !== ''
      );

      const videos = [
        ...(currentApplication.questionnaire?.applicationVideos || []),
        ...(currentApplication.questionnaire?.applicationVideo
          ? [currentApplication.questionnaire.applicationVideo]
          : []),
      ].filter(
        (url): url is string => typeof url === 'string' && url.trim() !== ''
      );

      setExistingResumes([...new Set(resumes)]);
      setExistingVideos([...new Set(videos)]);
    }
  }, [currentApplication, reset]);

  // Remove existing asset
  const removeExistingAsset = useCallback(
    (assetUrl: string, type: 'resume' | 'video') => {
      if (type === 'resume') {
        const newResumes = existingResumes.filter((url) => url !== assetUrl);
        setExistingResumes(newResumes);
      } else {
        const newVideos = existingVideos.filter((url) => url !== assetUrl);
        setExistingVideos(newVideos);
      }
    },
    [existingResumes, existingVideos]
  );

  const getAssetType = (type: 'resume' | 'video') => {
    if (type === 'resume') return 'resumes';
    if (type === 'video') return 'application-videos';
    return '';
  };

  // Handle new file uploads
  const handleNewFiles = useCallback(
    async (files: File[], type: 'resume' | 'video') => {
      if (!currentApplication?._id) {
        console.error('handleNewFiles: No current application ID');
        return;
      }

      const assetType = getAssetType(type);

      if (type === 'resume') {
        setIsResumeUploading(true);
      } else {
        setIsVideoUploading(true);
      }

      try {
        const uploadedKeys = await Promise.all(
          files.map((file) => uploadAsset(file, 'APPLICANTS', assetType))
        );

        if (type === 'resume') {
          setExistingResumes((prev) => {
            return [...prev, ...uploadedKeys];
          });
        } else {
          setExistingVideos((prev) => {
            return [...prev, ...uploadedKeys];
          });
        }

        toast.success('File uploaded successfully!');
      } catch (error) {
        toast.error('Failed to upload file. Please try again.');
      } finally {
        if (type === 'resume') {
          setIsResumeUploading(false);
        } else {
          setIsVideoUploading(false);
        }
      }
    },
    [currentApplication]
  );

  // Handle form submission
  const onSubmit = async (data: JobApplication) => {
    try {
      const cleanedData = {
        ...data,
        workExperience: {
          ...data.workExperience,
          resumes: existingResumes,
        },
        questionnaire: {
          ...data.questionnaire,
          applicationVideos: existingVideos,
        },
      };

      updateJobApplicationMutate(cleanedData);
    } catch (error) {
      console.error('onSubmit: Error submitting form', error);
      toast.error('Error submitting form. Please try again.');
    }
  };

  const inputClassName = `w-full rounded-md p-2 ${
    isDarkMode
      ? 'bg-gray-700 border-gray-600 text-gray-100 focus:border-indigo-500 focus:ring-indigo-500'
      : 'border border-gray-300 focus:border-indigo-500 focus:ring-indigo-500'
  }`;

  const labelClassName = `font-medium text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`;

  const renderMetadataField = (
    fieldName: string,
    value: string | string[] | undefined
  ) => {
    const fieldType = Array.isArray(value) ? 'multiselect' : 'text';
    const options =
      fieldType === 'multiselect' && Array.isArray(value) ? value : [];

    switch (fieldType) {
      case 'multiselect':
        return (
          <div key={fieldName} className="flex flex-col space-y-2">
            <label className={labelClassName}>
              {fieldName
                .split('_')
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ')}
            </label>
            <Controller
              name={`metadata.${fieldName}` as const}
              control={control}
              defaultValue={value as string[]}
              render={({ field: { onChange, value } }) => (
                <SelectComponent
                  isMulti
                  isCreatable
                  options={options.map((opt: string) => ({
                    label: opt,
                    value: opt,
                  }))}
                  value={
                    Array.isArray(value)
                      ? value.map((v) => ({ label: v, value: v }))
                      : []
                  }
                  onChange={(
                    newValue:
                      | MultiValue<SelectOption>
                      | SingleValue<SelectOption>
                  ) => {
                    if (Array.isArray(newValue)) {
                      onChange(newValue.map((item) => item.value));
                    } else {
                      onChange([]);
                    }
                  }}
                  onCreateOption={(inputValue: string) => {
                    const currentValues = Array.isArray(value) ? value : [];
                    onChange([...currentValues, inputValue]);
                  }}
                  className={`${inputClassName} !border-none !p-0`}
                  placeholder={`Select or type ${fieldName
                    .split('_')
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ')}`}
                />
              )}
            />
          </div>
        );

      default:
        return (
          <div key={fieldName} className="flex flex-col space-y-2">
            <label className={labelClassName}>
              {fieldName
                .split('_')
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ')}
            </label>
            <input
              type="text"
              {...register(`metadata.${fieldName}` as const)}
              defaultValue={value as string}
              className={inputClassName}
            />
          </div>
        );
    }
  };

  const isKimOrClientSelected =
    watch('status') === 'KimSelected' || watch('status') === 'ClientSelected';

  return (
    <div className="relative h-full max-w-5xl mx-auto">
      <BtnDefault
        type="button"
        onClick={() => navigate(-1)}
        className="flex items-center gap-1 my-4"
      >
        <ArrowLeft size={14} />
        Back
      </BtnDefault>
      <form onSubmit={handleSubmit(onSubmit)} className="h-full">
        <Section title="Job Details">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="flex flex-col space-y-2">
              <label className={labelClassName}>Job ID</label>
              <input
                {...register('jobid')}
                type="text"
                className={`${inputClassName} cursor-not-allowed bg-opacity-60`}
                readOnly
              />
            </div>
            <div className="flex flex-col space-y-2">
              <label className={labelClassName}>Status</label>
              <select
                {...register('status')}
                className={`${inputClassName} cursor-pointer`}
              >
                <option value={currentApplication?.status}>
                  {currentApplication?.status}
                </option>
                <option value="Rejected">Rejected</option>
                <option value="InterviewPending">Interview Pending</option>
                <option value="ToBeActioned">To Be Actioned</option>
                <option value="NoShow">No Show</option>
                <option value="CandidateNotInterested">
                  Candidate Not Interested
                </option>
                <option value="OnHold">On Hold - Prov Selected</option>
                <option value="ClientSelected">Client Selected</option>
                <option value="KimSelected">Kim Selected</option>
                <option value="Resigned">Resigned</option>
                <option value="SeparatedByOps">Separated by Ops</option>
              </select>
            </div>
            <div className="flex flex-col space-y-2">
              <label className={labelClassName}>Category</label>
              <input
                {...register('category')}
                type="text"
                className={`${inputClassName} cursor-not-allowed bg-opacity-60`}
                readOnly
              />
            </div>
            <div className="flex flex-col space-y-2">
              <label className={labelClassName}>Testlify Score</label>
              <input
                {...register('testlifyScore')}
                type="text"
                className={inputClassName}
              />
            </div>
            <div className="flex flex-col space-y-2">
              <label className={labelClassName}>Screening Status</label>
              <input
                {...register('screeningStatus')}
                type="text"
                className={inputClassName}
              />
            </div>

            {isKimOrClientSelected && (
              <div className="flex flex-col space-y-2 mt-6">
                <label className={labelClassName}>View Full Profile</label>

                <div className="flex items-center gap-4 mt-2">
                  <UrlPreview url={`/agents?email=${watch('email')}`} />
                </div>
              </div>
            )}
          </div>
        </Section>

        <Section title="Personal Information">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex flex-col space-y-2">
              <label className={labelClassName}>First Name</label>
              <input
                {...register('personalInfo.firstName')}
                type="text"
                className={inputClassName}
              />
            </div>
            <div className="flex flex-col space-y-2">
              <label className={labelClassName}>Middle Name</label>
              <input
                {...register('personalInfo.middleName')}
                type="text"
                className={inputClassName}
              />
            </div>
            <div className="flex flex-col space-y-2">
              <label className={labelClassName}>Last Name</label>
              <input
                {...register('personalInfo.lastName')}
                type="text"
                className={inputClassName}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
            <div className="flex flex-col space-y-2">
              <label className={labelClassName}>Date of Birth</label>
              <input
                {...register('personalInfo.dob')}
                type="date"
                className={inputClassName}
              />
            </div>
            <div className="flex flex-col space-y-2">
              <label className={labelClassName}>Family Members</label>
              <input
                {...register('personalInfo.familyMembers')}
                type="text"
                className={inputClassName}
              />
            </div>
            <div className="flex flex-col space-y-2">
              <label className={labelClassName}>Dependents</label>
              <input
                {...register('personalInfo.dependents')}
                type="text"
                className={inputClassName}
              />
            </div>
            <div className="flex flex-col space-y-2 mt-6">
              <label className={labelClassName}>Profile Picture URL</label>
              {watch('personalInfo.profilePic') && (
                <div className="flex items-center gap-4 mt-2">
                  <UrlPreview url={watch('personalInfo.profilePic')} />
                </div>
              )}
            </div>
            <div className="flex flex-col space-y-2 md:col-span-2">
              <label className={labelClassName}>Highest Qualification</label>
              <input
                {...register('personalInfo.highestEducation')}
                type="text"
                className={inputClassName}
              />
            </div>
          </div>
        </Section>

        <Section title="Contact Information">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex flex-col space-y-2">
              <label className={labelClassName}>Email</label>
              <input
                {...register('email')}
                type="email"
                className={inputClassName}
              />
            </div>
            <div className="flex flex-col space-y-2">
              <label className={labelClassName}>WhatsApp Number</label>
              <Controller
                name="contactInfo.wa"
                control={control}
                render={({ field }) => (
                  <PhoneNumberInput
                    value={field.value?.number}
                    onChange={(phone, data) => {
                      field.onChange({
                        number: phone,
                        code: data.country.dialCode,
                        countryEnum: data.country.iso2,
                      });
                    }}
                  />
                )}
              />
            </div>
            <div className="flex flex-col space-y-2">
              <label className={labelClassName}>Address Line</label>
              <input
                {...register('contactInfo.address.line')}
                type="text"
                className={inputClassName}
              />
            </div>
            <div className="flex flex-col space-y-2">
              <label className={labelClassName}>City</label>
              <input
                {...register('contactInfo.address.city')}
                type="text"
                className={inputClassName}
              />
            </div>
            <div className="flex flex-col space-y-2">
              <label className={labelClassName}>State</label>
              <input
                {...register('contactInfo.address.state')}
                type="text"
                className={inputClassName}
              />
            </div>
            <div className="flex flex-col space-y-2">
              <label className={labelClassName}>Country</label>
              <input
                {...register('contactInfo.address.country')}
                type="text"
                className={inputClassName}
              />
            </div>
            <div className="flex flex-col space-y-2">
              <label className={labelClassName}>ZIP Code</label>
              <input
                {...register('contactInfo.address.zip')}
                type="text"
                className={inputClassName}
              />
            </div>
          </div>
        </Section>

        <Section title="Work Experience">
          <div className="space-y-6">
            {workExperienceFields.map((field, index) => (
              <div
                key={field.id}
                className={`space-y-4 p-4 border rounded-lg relative ${
                  isDarkMode
                    ? 'border-gray-700 bg-gray-800'
                    : 'border-gray-200 bg-gray-50'
                }`}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex flex-col gap-4">
                    <div className="flex flex-col space-y-2">
                      <label className={labelClassName}>Company Name</label>
                      <input
                        {...register(
                          `workExperience.pastEmployers.${index}.employerName`
                        )}
                        type="text"
                        className={inputClassName}
                      />
                    </div>
                    <div className="flex flex-col space-y-2">
                      <label className={labelClassName}>Company URL</label>
                      <input
                        {...register(
                          `workExperience.pastEmployers.${index}.employerUrl`
                        )}
                        type="text"
                        className={inputClassName}
                      />
                      <UrlPreview
                        url={safeLink(
                          watch(
                            `workExperience.pastEmployers.${index}.employerUrl`
                          )
                        )}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex flex-col space-y-2">
                      <label className={labelClassName}>Start Month/Year</label>
                      <select
                        {...register(
                          `workExperience.pastEmployers.${index}.startMonth`
                        )}
                        className={inputClassName}
                      >
                        <option value="">Month</option>
                        {monthNames.map((month) => (
                          <option key={month} value={month}>
                            {month}
                          </option>
                        ))}
                      </select>
                      <select
                        {...register(
                          `workExperience.pastEmployers.${index}.startYear`
                        )}
                        className={inputClassName}
                      >
                        <option value="">Year</option>
                        {Array.from(
                          { length: 50 },
                          (_, i) => new Date().getFullYear() - i
                        ).map((year) => (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="flex flex-col space-y-2">
                      <label className={labelClassName}>End Month/Year</label>
                      <select
                        {...register(
                          `workExperience.pastEmployers.${index}.endMonth`
                        )}
                        className={inputClassName}
                      >
                        <option value="">Month</option>
                        {monthNames.map((month) => (
                          <option key={month} value={month}>
                            {month}
                          </option>
                        ))}
                      </select>
                      <select
                        {...register(
                          `workExperience.pastEmployers.${index}.endYear`
                        )}
                        className={inputClassName}
                      >
                        <option value="">Year</option>
                        {Array.from(
                          { length: 50 },
                          (_, i) => new Date().getFullYear() - i
                        ).map((year) => (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => removeWorkExperience(index)}
                  className="absolute -top-6 -right-2 hover:text-red-400"
                >
                  <FaTrashAlt className="h-4 w-4" />
                </button>
              </div>
            ))}
            <BtnDefault
              type="button"
              onClick={() =>
                appendWorkExperience({
                  employerName: '',
                  employerUrl: '',
                  startMonth: '',
                  startYear: '',
                  endMonth: '',
                  endYear: '',
                })
              }
              className="flex items-center gap-2"
            >
              <IoAddCircleOutline className="h-5 w-5" />
              Add Work Experience
            </BtnDefault>
          </div>
        </Section>

        <Section title="Skills & Qualifications">
          <div className="space-y-6">
            {/* Existing Resumes */}
            {existingResumes.length > 0 && (
              <div>
                <h3 className={`text-lg font-semibold mb-4 ${labelClassName}`}>
                  Current Resumes
                </h3>
                <div className="space-y-2">
                  {existingResumes.map((resumeUrl, index) => (
                    <div
                      key={index}
                      className={`flex items-center justify-between p-2 rounded-md border ${
                        isDarkMode
                          ? 'bg-gray-700 border-gray-600'
                          : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      <span
                        className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}
                      >
                        Resume {index + 1}
                      </span>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => window.open(resumeUrl, '_blank')}
                          className="text-blue-500 hover:text-blue-700 p-1"
                        >
                          <FaEye size={16} />
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            removeExistingAsset(resumeUrl, 'resume')
                          }
                          className="text-red-500 hover:text-red-700 p-1"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* New Resume Upload */}
            <FileUpload
              title="Upload Resumes"
              files={[]}
              onFilesChange={(files) => handleNewFiles(files, 'resume')}
              accept=".pdf,.doc,.docx"
              maxSize={10}
              maxFiles={5}
              isUploading={isResumeUploading}
            />

            {/* Existing Videos */}
            {existingVideos.length > 0 && (
              <div>
                <h3 className={`text-lg font-semibold mb-4 ${labelClassName}`}>
                  Current Application Videos
                </h3>
                <div className="space-y-2">
                  {existingVideos.map((videoUrl, index) => (
                    <div
                      key={index}
                      className={`flex items-center justify-between p-3 rounded-lg border ${
                        isDarkMode
                          ? 'bg-gray-700 border-gray-600'
                          : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      <span
                        className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}
                      >
                        Video {index + 1}
                      </span>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => window.open(videoUrl, '_blank')}
                          className="text-blue-500 hover:text-blue-700 p-1"
                        >
                          <FaEye size={16} />
                        </button>
                        <button
                          type="button"
                          onClick={() => removeExistingAsset(videoUrl, 'video')}
                          className="text-red-500 hover:text-red-700 p-1"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* New Video Upload */}
            <FileUpload
              title="Upload Application Videos"
              files={[]}
              onFilesChange={(files) => handleNewFiles(files, 'video')}
              accept=".mp4,.mov,.avi,.mkv,.webm"
              maxSize={300}
              maxFiles={3}
              isUploading={isVideoUploading}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex flex-col space-y-2">
                <label className={labelClassName}>
                  When can you begin work?
                </label>
                <input
                  {...register('workExperience.beginWork')}
                  type="text"
                  className={inputClassName}
                />
              </div>
              <div className="flex flex-col space-y-2">
                <label className={labelClassName}>Industry</label>
                <input
                  {...register('industry')}
                  type="text"
                  className={inputClassName}
                  placeholder="Enter industry"
                />
              </div>
            </div>
            <div className="flex flex-col space-y-2 md:col-span-2">
              <label className={labelClassName}>Relevant Industries</label>
              <Controller
                name="relevantIndustries"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <TagInput
                    placeholder="Type relevant industry and press Enter"
                    tags={value || []}
                    onEnter={(e: React.KeyboardEvent<HTMLInputElement>) => {
                      const target = e.target as HTMLInputElement;
                      const newTag = target.value.trim();
                      if (newTag && !value?.includes(newTag)) {
                        onChange([...(value || []), newTag]);
                        target.value = '';
                      }
                    }}
                    onRemoveTag={(index: number) => {
                      const newTags = [...(value || [])];
                      newTags.splice(index, 1);
                      onChange(newTags);
                    }}
                  />
                )}
              />
            </div>
          </div>
        </Section>

        <Section title="Languages">
          <div className="space-y-4">
            {languageFields.map((field, index) => (
              <div
                key={field.id}
                className="flex items-center gap-4 flex-wrap md:flex-nowrap"
              >
                <select
                  {...register(`questionnaire.languages.${index}.language`)}
                  className={`${inputClassName} min-w-[200px] max-w-sm`}
                >
                  <option value="">Select Language</option>
                  {languageOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>

                <Controller
                  name={`questionnaire.languages.${index}.fluency`}
                  control={control}
                  render={({ field }) => (
                    <SelectComponent
                      {...field}
                      isMulti
                      options={fluencyOptions}
                      className="min-w-[200px] flex-1"
                      value={fluencyOptions.filter((option) =>
                        field.value?.includes(option.value)
                      )}
                      onChange={(
                        newValue:
                          | MultiValue<SelectOption>
                          | SingleValue<SelectOption>
                      ) =>
                        field.onChange(
                          (newValue as MultiValue<SelectOption>).map(
                            (item) => item.value
                          )
                        )
                      }
                      placeholder="Select Fluency"
                    />
                  )}
                />

                <button
                  onClick={() => removeLanguage(index)}
                  className="!p-0 hover:text-red-400"
                >
                  <FaTrashAlt className="h-4 w-4" />
                </button>
              </div>
            ))}
            <BtnDefault
              type="button"
              onClick={() => appendLanguage({ language: '', fluency: [] })}
              className="flex items-center gap-2 mt-4"
            >
              <IoAddCircleOutline className="h-5 w-5" />
              Add Language
            </BtnDefault>
          </div>
        </Section>

        <Section title="Source & Referral">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex flex-col space-y-2">
              <label className={labelClassName}>Job Source</label>
              <input
                {...register('source')}
                type="text"
                className={inputClassName}
              />
            </div>
            <div className="flex flex-col space-y-2">
              <label className={labelClassName}>Referral</label>
              <input
                {...register('referral')}
                type="email"
                className={inputClassName}
              />
            </div>
          </div>
        </Section>

        <Section title="Interview Notes">
          <div className="flex flex-col space-y-2">
            <label className={labelClassName}>Notes</label>
            <textarea
              {...register('interviewNotes')}
              className={inputClassName}
              rows={5}
            />
          </div>
        </Section>

        {currentApplication?.metadata &&
          Object.keys(currentApplication.metadata).length > 0 && (
            <Section title="Additional Information">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {Object.entries(currentApplication.metadata).map(
                  ([fieldName, value]) => renderMetadataField(fieldName, value)
                )}
              </div>
            </Section>
          )}

        <div className={`sticky top-0 z-20 flex justify-end w-full mt-5`}>
          <BtnPrimary
            disabled={isPending || isResumeUploading || isVideoUploading}
            className="px-16"
            type="submit"
          >
            {isPending ? (
              <div className="flex items-center gap-2">
                <span className="ml-2">Updating</span>
                <LoaderCircle size={16} className="animate-spin" />
              </div>
            ) : (
              'Update'
            )}
          </BtnPrimary>
        </div>
      </form>
    </div>
  );
};

const Section: React.FC<{
  title: string;
  children: React.ReactNode;
  className?: string;
}> = ({ title, children, className = '' }) => {
  const { isDarkMode } = useThemeStore();
  return (
    <div
      className={`p-6 rounded-lg shadow-sm ${isDarkMode ? 'bg-gray-800' : 'bg-white'} ${className}`}
    >
      <h2
        className={`text-xl font-semibold mb-6 ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}
      >
        {title}
      </h2>
      {children}
    </div>
  );
};

export default UpdateJobApplication;
